import React, { useEffect, useMemo, useState } from 'react'
import { Gantt, GanttProps, Task } from 'gantt-task-react'

import 'gantt-task-react/dist/index.css'
import { createUseStyles, List, ListItem, ListItemIcon, ListItemText } from '@airbus/components-react/components'
import i18n from 'i18n'
import tools from 'utils/helpers'
import clsx from 'clsx'
import { useParams, useSearchParams } from 'react-router-dom'

const { t } = i18n

const useStyles = createUseStyles(
  ({ color }) => ({
    root: {
      '& ._2B2zv': {
        height: '120px !important',
      },
      '& ._1eT-t': {
        height: '120px !important',
      },
      '& ._KxSXS': {
        cursor: 'pointer',
      },
      '& ._35nLX': {
        // header
        fill: color.getModalValues(color.neutral.light),
      },
      '& ._9w8d5': {
        // calendar days
        fill: color.getModalValues(color.text.primary),
      },
      '& ._2q1Kt': {
        // calendar month
        fill: color.getModalValues(color.text.primary),
      },
      '& ._2dZTy': {
        // odd rows
        fill: color.getModalValues(color.background.primary),
      },
      '& ._2dZTy:nth-child(even):not(:hover)': {
        // even rows
        fill: color.getModalValues(color.neutral.lighter),
      },
      '& ._2dZTy:hover': {
        // hover rows
        fill: color.getModalValues(color.neutral.light),
      },
      '& ._3KcaM': {
        // tasks text
        fill: color.getModalValues(color.text.primary),
      },
    },
  }),
  'gantt',
)

function SafeImage({ src, alt, ...props }: { src: string; alt: string }) {
  const [error, setError] = useState(false)

  if (error || !src) {
    return <div>{alt}</div>
  }

  return (
    <ListItemIcon>
      <img src={src} alt={alt} onError={() => setError(true)} style={{ width: '5em', height: '1.5em' }} {...props} />
    </ListItemIcon>
  )
}

const TaskListTable = ({
  tasks,
  rowHeight,
  rowWidth,
  selectedTaskId,
  setSelectedTask,
}: {
  tasks: Task[]
  rowHeight: number
  rowWidth: string
  selectedTaskId: string
  setSelectedTask: (taskId: string) => void
}) => {
  const aircrafts = useMemo(() => tools.groupByKey(tasks, 'project'), [tasks])
  const [searchParams] = useSearchParams()

  const aircraft = searchParams.get('search')
  const { bdvnumdoc } = useParams()

  useEffect(() => {
    if (aircraft && tasks) {
      let wpIndex = tasks?.findIndex((t) => t.project === aircraft)

      if (bdvnumdoc) {
        wpIndex = tasks?.findIndex((t) => t.name === bdvnumdoc && t.project === aircraft)
      }
      const selectedWp = tasks[wpIndex]
      if (selectedWp) {
        if (bdvnumdoc) setSelectedTask(selectedWp.id)
        const nodeListScrollBar = document.querySelectorAll('._1eT-t')
        const offsetTopForAircraft = wpIndex * rowHeight - 75

        document.querySelectorAll('._2B2zv')[0].scrollBy(0, offsetTopForAircraft)
        document.querySelectorAll('._2B2zv')[1].scrollBy(0, offsetTopForAircraft)

        if (nodeListScrollBar.length > 0) nodeListScrollBar[0].scrollBy({ top: offsetTopForAircraft })
      }
    }
  }, [tasks, aircraft, bdvnumdoc])

  return (
    <List style={{ marginTop: '8px', minWidth: 'unset' }}>
      {aircrafts.map(({ project, items }) => (
        <ListItem
          key={items[0].id}
          id={project}
          selected={(selectedTaskId?.split('_')?.[1] || aircraft) === project}
          style={{
            height: items?.length ? rowHeight * items.length : rowHeight,
            width: rowWidth,
            border: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          <SafeImage src={'data:image;base64,' + items[0].imgSrc} alt={items[0].applicability} />
          <div>
            <ListItemText>{project}</ListItemText>
          </div>
        </ListItem>
      ))}
    </List>
  )
}

const TaskListHeader = ({ headerHeight }: { headerHeight: number }) => (
  <div style={{ height: headerHeight }}>{t('aircraft')}</div>
)

const Planning = ({ tasks, ...rest }: GanttProps) => {
  const classes = useStyles()
  return (
    <div className={clsx(classes.root)}>
      <Gantt tasks={tasks} {...rest} TaskListTable={TaskListTable} TaskListHeader={TaskListHeader} />
    </div>
  )
}

export default Planning
